"use client";

import { EditorUnsavedChangesDialog } from "@/components/editor/editor-unsaved-changes-dialog";
import { EditorUnsavedChangesDialog as LegacyEditorUnsavedChangesDialog } from "@/components/editor-legacy/editor-unsaved-changes-dialog";
import { isLegacyEditorPath } from "@/components/editor-legacy/legacy-editor-href";
import { AppSidebar } from "@/components/app-sidebar";
import { SettingsSidebar } from "@/components/settings-sidebar";
import { Header } from "@/components/header";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  SidebarPreferencesProvider,
  useSidebarPreferences,
} from "@/contexts/sidebar-preferences-context";
import { PrefetchedPermissionsProvider } from "@/contexts/prefetched-permissions-context";
import { SidebarPreferences } from "@/lib/sidebar-preferences";
import type { PrefetchedPermissions } from "@/lib/permission-checks";
import { isStudioPath } from "@/utils/editor-href";
import { cn } from "@/utils/utils";
import { MAIN_SCROLL_CONTAINER_ID } from "@/lib/main-scroll-container";
import { useShopStore } from "@/store/shopStore";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useAeoBrainGlobalGenerationSync } from "@/hooks/use-aeo-brain-global-generation-sync";
import { AIGlobalProvider } from "./ai-global-provider";
import { ShopEventsProvider } from "./shop-events-provider";
import { PylonProvider } from "./pylon-provider";
import { ShopRealtimeHandlers } from "./realtime/shop-realtime-handlers";
import { SubdomainProvisioner } from "./subdomain-provisioner";
import { ShopSummary, ShopType } from "@/types/shop";
import { useSessionContext } from "@/contexts/session-context";
import { OAuthConsentScreen } from "@/components/oauth/consent-screen";
import { isSlackPath } from "@/constants/slack";

function AppLayoutContent({
  children,
  shop,
  isAdmin,
  isEditor,
  isAiPage,
  isSettingsPage,
  sidebarTransitionsEnabled,
}: {
  children: React.ReactNode;
  shop: ShopSummary;
  isAdmin: boolean;
  isEditor: boolean;
  isAiPage: boolean;
  isSettingsPage: boolean;
  sidebarTransitionsEnabled: boolean;
}) {
  const { open: isSidebarOpen } = useSidebar();
  const isHomePage = usePathname() === "/home";

  return (
    <>
      {isSettingsPage && !isEditor ? (
        <SettingsSidebar shop={shop} className={cn(isAdmin && "pt-[30px]")} />
      ) : (
        <AppSidebar shop={shop} className={cn(isAdmin && "pt-[30px]")} />
      )}
      <SidebarInset
        className={cn(
          "!overflow-hidden !bg-inner-background !shadow-none md:!my-3 md:!rounded-xl md:border",
          sidebarTransitionsEnabled && "transition-[margin] duration-300",
          isSidebarOpen ? "md:!ml-0 md:!mr-3" : "md:!mx-3",
          isEditor && "!border-none !bg-secondary md:!m-0 md:!rounded-none",
        )}
      >
        {!isEditor && !isHomePage && (
          <div className="z-10 flex justify-center">
            <Header />
          </div>
        )}

        <main
          id={MAIN_SCROLL_CONTAINER_ID}
          className={cn(
            "no-scrollbar flex h-full w-full flex-col justify-start overflow-y-auto p-4",
            (isEditor || isAiPage) && "overflow-hidden p-0",
            isAiPage && "pt-5",
          )}
        >
          {children}
        </main>
      </SidebarInset>
    </>
  );
}

function SidebarLayout({
  children,
  shop,
  isAdmin,
  isEditor,
  isAiPage,
  isSettingsPage,
}: {
  children: React.ReactNode;
  shop: ShopSummary;
  isAdmin: boolean;
  isEditor: boolean;
  isAiPage: boolean;
  isSettingsPage: boolean;
}) {
  const { preferences, setOpen } = useSidebarPreferences();
  const pathname = usePathname();
  const [sidebarTransitionsEnabled, setSidebarTransitionsEnabled] =
    useState(false);
  const forceSidebarClosed = isEditor;
  // Settings always shows its sidebar on desktop, even if the main nav was collapsed.
  // Mobile still uses the offcanvas sheet via openMobile.
  const sidebarOpen = forceSidebarClosed
    ? false
    : isSettingsPage
      ? true
      : preferences.open;

  useEffect(() => {
    setSidebarTransitionsEnabled(true);
  }, []);

  return (
    <SidebarProvider
      className={cn(isAdmin && "h-full min-h-0")}
      open={sidebarOpen}
      forceDrawer={isEditor}
      transitionsEnabled={sidebarTransitionsEnabled}
      onOpenChange={(open) => {
        if (forceSidebarClosed || isSettingsPage) {
          return;
        }
        setOpen(open);
      }}
    >
      {isLegacyEditorPath(pathname) ? (
        <LegacyEditorUnsavedChangesDialog />
      ) : (
        <EditorUnsavedChangesDialog />
      )}
      <AppLayoutContent
        shop={shop}
        isAdmin={isAdmin}
        isEditor={isEditor}
        isAiPage={isAiPage}
        isSettingsPage={isSettingsPage}
        sidebarTransitionsEnabled={sidebarTransitionsEnabled}
      >
        {children}
      </AppLayoutContent>
    </SidebarProvider>
  );
}

export default function Providers({
  children,
  shop,
  initialSidebarPreferences,
  prefetchedPermissions,
}: {
  children: React.ReactNode;
  shop: ShopSummary;
  initialSidebarPreferences: SidebarPreferences;
  prefetchedPermissions: PrefetchedPermissions;
}) {
  const pathname = usePathname();

  const setShopId = useShopStore((state) => state.setShopId);
  const setShopDomain = useShopStore((state) => state.setShopDomain);
  const setShopType = useShopStore((state) => state.setShopType);
  const setShopName = useShopStore((state) => state.setShopName);
  const setShopFavicon = useShopStore((state) => state.setShopFavicon);
  useAeoBrainGlobalGenerationSync();

  const { isAdmin } = useSessionContext();

  useEffect(() => {
    if (shop?.domain) {
      setShopId(shop.id);
      setShopDomain(shop.domain);
      setShopType(shop.shopType as ShopType);
      setShopName(shop.name);
      setShopFavicon(shop.favicon ?? "");
    }
  }, [
    setShopDomain,
    setShopFavicon,
    setShopId,
    setShopName,
    setShopType,
    shop.domain,
    shop.favicon,
    shop.id,
    shop.name,
    shop.shopType,
  ]);

  const isOAuthConsent = pathname.startsWith("/oauth/authorize");
  const isSlackConnect = isSlackPath(pathname);
  const isSettingsPage = pathname.includes("/settings");
  const isEditor = pathname.includes("/editor/") || isStudioPath(pathname);
  const isAiPage = pathname.startsWith("/ai/");

  if (isOAuthConsent) {
    return <OAuthConsentScreen />;
  }

  // Slack consent flow: render the standalone page (server component handles its own layout).
  if (isSlackConnect) {
    return <>{children}</>;
  }

  return (
    <ShopEventsProvider shopId={shop.id}>
      <ShopRealtimeHandlers />
      <PylonProvider />
      <AIGlobalProvider>
        <SubdomainProvisioner
          shopId={shop.id}
          pendingSubdomain={shop.pendingSubdomain ?? null}
        />
        <div className="relative flex h-screen min-h-svh w-full flex-col bg-background">
          {isAdmin && (
            <div className="flex w-full animate-gradient items-center justify-center bg-[linear-gradient(to_right,theme(colors.purple.400),theme(colors.purple.100),theme(colors.pink.300),theme(colors.orange.400),theme(colors.pink.300),theme(colors.purple.100),theme(colors.purple.400))] bg-[length:200%_auto] p-[1px]">
              <p className="text-xs font-medium">Production Admin</p>
            </div>
          )}
          <PrefetchedPermissionsProvider permissions={prefetchedPermissions}>
            <SidebarPreferencesProvider
              initialPreferences={initialSidebarPreferences}
            >
              <SidebarLayout
                shop={shop}
                isAdmin={isAdmin}
                isEditor={isEditor}
                isAiPage={isAiPage}
                isSettingsPage={isSettingsPage}
              >
                {children}
              </SidebarLayout>
            </SidebarPreferencesProvider>
          </PrefetchedPermissionsProvider>
        </div>
      </AIGlobalProvider>
    </ShopEventsProvider>
  );
}
